import { useEffect, useState } from 'react';
import Loader from 'components/shared/Loader';
import useApp from 'hooks/useApp';
import classNames from 'utilities/ClassNames';
import InvoicesHeader from 'components/invoices/InvoicesHeader';
import InvoicesFilters from 'components/invoices/InvoicesFilters';
import InvoicesTable from 'components/invoices/InvoicesTable';
import InvoiceModal from 'components/Modal/InvoiceModal';
import ConfirmationModal from 'components/Modal/ConfirmationModal';
import ViewInvoiceModal from 'components/Modal/InvoiceDetailModal';


interface Invoice {
  id: string;
  invoiceNumber: string;
  customerName: string;
  subscriptionType: string;
  inoviceDate: string;
  amount: string;
  dueDate: string;
  paymentStatus: string;
  paymentMethod: string;
}

export default function Invoices() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const { isScaled } = useApp();
  const [refreshMode, setRefreshMode] = useState('');
  const [editInvoice, setEditInvoice] = useState<Invoice | null>(null);
  const [deleteInvoice, setDeleteInvoice] = useState<Invoice | null>(null);
  const [viewInvoice, setViewInvoice] = useState<Invoice | null>(null);

  const [filters, setFilters] = useState({
    customerName: '',
    subscriptionType: '',
    paymentStatus: '',
    paymentMethod: '',
    startDate: null,
    endDate: null,
  });

  // Mock data fetching
  const fetchInvoices = async () => {
    setLoading(true);
    try {
      // Replace with actual API call
      const mockInvoices = [
        {
          id: '1',
          invoiceNumber: 'INV-001',
          customerName: 'John Doe',
          subscriptionType: 'Enterprise',
          inoviceDate: '12/03/2024 ',
          amount: '$999.99',
          dueDate: '02/15/2025',
          paymentStatus: 'Paid',
          paymentMethod: 'Credit Card',
        },
        {
          id: '2',
          invoiceNumber: 'INV-002',
          customerName: 'Michael Broe',
          subscriptionType: 'Enterprise',
          inoviceDate: '12/03/2025 ',
          amount: '$88.99',
          dueDate: '02/22/2026',
          paymentStatus: 'Pending',
          paymentMethod: 'Credit Card',
        },
        {
          id: '3',  
          invoiceNumber: 'INV-002',
          customerName: 'Jane Smith',
          subscriptionType: 'Basic',
          inoviceDate: '12/03/2023 ',
          amount: '$50.00',
          dueDate: '03/03/2024',
          paymentStatus: 'Failed',
          paymentMethod: 'Credit Card',
            
        }
        // Add more mock data as needed
      ];
      setInvoices(mockInvoices);
    } catch (err) {
      setError(true);
      console.error('Error fetching invoices:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  useEffect(() => {
    if (refreshMode === 'invoices') {
      fetchInvoices();
      setRefreshMode('');
    }
  }, [refreshMode]);

  const handleDelete = async () => {
    if (deleteInvoice) {
      try {
        // Replace with actual API call
        setInvoices(invoices.filter(inv => inv.id !== deleteInvoice.id));
        setRefreshMode('invoices');
      } catch (error) {
        console.error('Error deleting invoice:', error);
      }
      setDeleteInvoice(null);
    }
  };

  return (
    <>
      {loading && (
        <div className="w-full h-[80vh] grid place-content-center">
          <Loader />
        </div>
      )}
      {error && (
        <div className="w-full h-[80vh] grid place-content-center">
          <p>Error loading data</p>
        </div>
      )}
      {!loading && !error && (
        <div className={classNames(
          isScaled ? "text-base" : "text-base",
          "px-6 pb-3 overflow-hidden overflow-y-auto h-full relative"
        )}>
          <div className="pt-6 mb-6">
            <InvoicesHeader />
          </div>

          {/* Sticky header and filters */}
          <div className="sticky top-0 z-10 bg-[#232323] pb-1 pt-2">
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-xl font-semibold text-white">All Invoices</h1>
            </div>

            <InvoicesFilters
              setRefreshMode={setRefreshMode}
              setFilters={setFilters}
              filters={filters}
              setViewInvoice={setViewInvoice}
              invoices={invoices}
            />
          </div>

          {/* Table content with margin top for spacing */}
          <div className="mt-4">
            <InvoicesTable
              invoices={invoices}
              setRefreshMode={setRefreshMode}
              filters={filters}
              onEdit={setEditInvoice}
              onDelete={setDeleteInvoice}
              onView={setViewInvoice}
            />
          </div>
        </div>
      )}

      <InvoiceModal
        isOpen={!!editInvoice}
        onClose={() => setEditInvoice(null)}
        mode="edit"
        initialData={editInvoice}
        setRefreshMode={setRefreshMode}
      />

      <ViewInvoiceModal
        isOpen={!!viewInvoice}
        onClose={() => setViewInvoice(null)}
        invoice={viewInvoice}
      />

      <ConfirmationModal
        isOpen={!!deleteInvoice}
        message="Are you sure you want to delete this invoice?"
        onConfirm={handleDelete}
        onCancel={() => setDeleteInvoice(null)}
      />
    </>
  );
}
